import { Homeowner } from "@app/models/Homeowner";
import { RegistrationApi } from "@app/services/apiServices/RegistrationApi";
import { OauthService } from "@app/services/oauth.service";
import { NetworkErrorService } from "@app/services/NetworkError.service";
import { CountryCallingCodeApi } from "@app/services/apiServices/CountryCallingCodeApi";
import { CountryCode } from "@app/models/CountryCode";
import { RegistrationStore } from "@app/pages/registration/stores/RegistrationStore";
import { FeaturesService } from "@app/services/Features.service";
import { take } from "rxjs/operators";
import {
    AfterViewInit,
    Component,
    ElementRef,
    EventEmitter,
    Inject,
    Input,
    OnInit,
    Output,
    ViewChild
} from "@angular/core";
import { WindowToken } from "@app/util/window.token";
import { AppDataService } from "@app/services/appData.service";
import { MatDialog } from "@angular/material/dialog";
import { PhonePipe } from "@app/pipes/phone-pipe";
import { Router } from "@angular/router";
import { CommonService } from "@app/services/CommonService";

export interface PersonalInfoData {
    homeowner?: Homeowner;
    emailAddressExists?: boolean;
    emailAddressError?: boolean;
    emailAddressErrorMessage?: string;
    isHoaHomeowner?: boolean;
    useAnonymousApi?: boolean;
    accountActive?: boolean;
    isValidatingEmail?: boolean;
    registration?: boolean;
    registeredUserEnabled?: boolean;
    showErrorMessage?: boolean;
    showRequired?: boolean;
    registrationType?: string;
    emailAddressLookupExecuted?: boolean;

}

/**
 * Created by mac85103 on 12/6/16.
 */
export class PersonalInfo implements OnInit, AfterViewInit {

    @Input() personalInfoData: PersonalInfoData;

    @Output() invalidCheck = new EventEmitter<Object>();

    @ViewChild('phoneNumberInput') private phoneNumberInput: ElementRef;

    firstNameError: boolean;
    showFirstNameInput: boolean = false;
    lastNameError: boolean;
    showLastNameInput: boolean = false;
    loggingIn: boolean;
    phoneNumberError: boolean = false;
    showPhoneNumberInput: boolean = false;
    countryCode: CountryCode;
    countryCodes: CountryCode[] = [];
    // need this for phone number bug on android.
    phoneElement: any;
    registeredUserMessage: string;
    phoneNumberChanged: boolean;

    constructor(
        @Inject(RegistrationApi) private registrationApi: RegistrationApi,
        @Inject(WindowToken) private $window: any,
        @Inject(OauthService) private oauthService: OauthService,
        @Inject(AppDataService) private appDataService: AppDataService,
        @Inject(NetworkErrorService) private networkErrorService: NetworkErrorService,
        @Inject(CountryCallingCodeApi) private countryCallingCodeApi: CountryCallingCodeApi,
        @Inject(RegistrationStore) private registrationStore: RegistrationStore,
        @Inject(MatDialog) private dialog: MatDialog,
        @Inject(FeaturesService) private featuresService: FeaturesService,
        @Inject(PhonePipe) private phonePipe: PhonePipe,
        @Inject(Router) private router: Router,
        @Inject(CommonService) private commonService: CommonService) {}

    ngOnInit() {

        this.personalInfoData.registeredUserEnabled = this.featuresService.isEnabled("REGISTERED_USERS")
        // get the
        if (this.personalInfoData && this.personalInfoData.registrationType === 'easy')
            this.registeredUserMessage = "The email address entered indicates you are a registered user. " +
                "Login to access your account.";
        else if (this.personalInfoData && this.personalInfoData.registration)
            this.registeredUserMessage = "The email address entered indicates you are a registered user. " +
                "Login to access your account or enter a different email address.";
        else
            this.registeredUserMessage = "The email address entered indicates you are a registered user. " +
                "Login to make a payment as a registered user or continue to make a payment as a guest.";

        const homeowner = this.registrationStore.getHomeowner();
        // for validating and setting the pre-populated fields from URL params
        if (homeowner !== undefined) {
            this.personalInfoData.homeowner = homeowner;
            this.personalInfoData.homeowner.firstName ? this.firstName() : null;
            this.personalInfoData.homeowner.lastName ? this.lastName() : null;
        }

        this.countryCallingCodeApi.getAllCountries(this.personalInfoData.useAnonymousApi)
            .pipe(take(1)).subscribe((data) => {
                this.setCountryCodes(data.data);
                if (this.personalInfoData.registrationType === 'easy') {
                    this.checkPhoneNumberLength();
                    if (this.phoneNumberError)
                        this.showPhoneNumberInput = true;
                }
            });

        if (this.personalInfoData.registrationType === 'easy') {
            this.firstName();
            this.lastName();

            if (this.firstNameError)
                this.showFirstNameInput = true;
            if (this.lastNameError)
                this.showLastNameInput = true;
        }

    }

    ngAfterViewInit() {
        if (this.phoneNumberInput) {
            this.phoneElement = this.phoneNumberInput.nativeElement;
        }
    }

    setCountryCodes(countryCodes) {
        this.countryCodes = countryCodes;
        const phone0 = this.personalInfoData.homeowner.contactInformation.phones[0];
        const defaultCountryCode =
            this.countryCodes.filter(x => x.country === (phone0.countryCallingCode ? phone0.countryCallingCode : "USA"))[0]; //default to USA
        this.countryCode = defaultCountryCode;
        this.formatPhoneNumber()
    }

    onCountryCodeChange() {
        this.formatPhoneNumber()
        try {
            // set the country code for the homeowner object
            this.personalInfoData.homeowner.contactInformation.phones[0].countryCallingCode = this.countryCode.country
        } catch (_dne) { }
    }

    firstName() {
        this.firstNameError = !this.personalInfoData.homeowner.firstName;
        if (!this.firstNameError)
            this.personalInfoData.homeowner.firstName = this.enforceCharacterLimit(this.personalInfoData.homeowner.firstName, 25)
    }

    lastName() {
        this.lastNameError = !this.personalInfoData.homeowner.lastName;
        if (!this.lastNameError)
            this.personalInfoData.homeowner.lastName = this.enforceCharacterLimit(this.personalInfoData.homeowner.lastName, 35)
    }

    enforceCharacterLimit(text, limit): string {
        if (text.length > limit) {
            text = text.substring(0, limit);
        }

        return text
    }

    login() {
        const webUrl = this.appDataService.appConfig.get_APP_ENV().HOAPASS_WEB_URL;
        this.$window.location = `${webUrl}/registered/#/properties`;
    }

    amui() {
        const webUrl = this.appDataService.appConfig.get_APP_ENV().AMUI_BASE_URL;
        this.$window.location = `${webUrl}`;
    }

    formatPhoneNumber() {
        const phone0 = this.personalInfoData.homeowner.contactInformation.phones[0];
        if ((phone0.value == null || phone0.value === "") && !this.phoneNumberChanged && this.personalInfoData.registrationType !== 'easy') {
            this.phoneNumberError = false;
        } else {
            this.phoneNumberChanged = true;
            if (!phone0.countryCallingCode)
                this.phoneNumberError = true;
            // set the country code on homeowner object
            phone0.countryCallingCode = this.countryCode.code;
            // make sure that homeowner.contactinformation.phones[0].value exists, the hard way.
            if (phone0.value) {
                this.phoneNumberChanged = true;
                let formattedPhoneNumber: string;
                let value: string = phone0.value;
                if (this.countryCode.country === "USA" || this.countryCode.country === "CAN") {
                    this.phoneNumberError = false;
                    value = value.replace(/[^0-9]/g, '');
                    if (value[0] === '0' || value[0] === '1') {
                        value = value.slice(1);
                    }
                    formattedPhoneNumber = this.phonePipe.transform(value);
                } else {
                    const countryCodeValue = this.countryCode.value || ""
                    formattedPhoneNumber = value.replace(/[^0-9]+/g, '')
                        .substr(0, 15 - countryCodeValue.length);
                }
                if (phone0.value !== formattedPhoneNumber) {
                    phone0.value = formattedPhoneNumber;
                }
                if (this.phoneElement && this.phoneElement[0]) this.phoneElement[0].value = formattedPhoneNumber

                this.checkPhoneNumberLength();
            }
            // force cursor to be at the end of phone number input all the time. We should only see a max of 14 characters or so, 99 is safe.
            if (this.phoneElement && this.phoneElement[0]) this.phoneElement[0].setSelectionRange(99, 99);
        }
    }

    checkPhoneNumberLength() {
        // make sure that homeowner.contactinformation.phones[0].value exists, the hard way.
        if (this.personalInfoData.homeowner &&
            this.personalInfoData.homeowner.contactInformation &&
            this.personalInfoData.homeowner.contactInformation.phones &&
            this.personalInfoData.homeowner.contactInformation.phones[0].value) {
            if (this.personalInfoData.homeowner.contactInformation.phones[0].value.replace(/[^0-9]+/g, '').length < 10 &&
                (this.countryCode.country === 'USA' || this.countryCode.country === 'CAN'))
                this.phoneNumberError = true;
            else
                this.phoneNumberError =
                    this.personalInfoData.homeowner.contactInformation.phones[0].value.replace(/[^0-9]+/g, '').length < 4;
        } else {
            this.phoneNumberError = true;
        }

    }

    onInvalidCheck(invalidObject: Object) {
        this.invalidCheck.emit(invalidObject);
    }
}

@Component({
    selector: 'app-personal-info',
    template: require('./personalInfo.directive.html')
})
export class PersonalInfoComponent extends PersonalInfo implements OnInit {

    ngOnInit() {
        this.personalInfoData.showErrorMessage = false
        this.personalInfoData.showRequired = true
        super.ngOnInit();
    }

}

@Component({
    selector: 'app-personal-info-easy',
    template: require('./personalInfoEasy.directive.html')
})
export class PersonalInfoEasyComponent extends PersonalInfo {

    ngOnInit() {
        this.personalInfoData.registrationType = "easy";
        this.personalInfoData.showErrorMessage = false;
        this.personalInfoData.showRequired = false;
        super.ngOnInit();
    }

}
